import { Book, Category, Dashboard } from '@apis/index'
import { Button, Card, GridCard, Row } from '@components'
import Column from '@components/column'
import Fields from '@components/fields'
import {
  StyleFormFieldWrapper,
  StyleFormRowWrapper,
} from '@components/fields/style'
import { IGridItem } from '@components/gridCard'
import {
  cardStyle,
  style38Percent,
  style60Percent,
  StyleCardTitle,
  StyleColumn,
} from '@components/styles'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const AdminBookManagementForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { state: book } = useLocation()
  const [loading, setLoading] = useState<boolean>(false)

  const { data: booksCategory } = useSelector(
    (state: any) => state.bookCategories,
  )
  const { data: dashboard } = useSelector((state: any) => state.bookDashboard)

  const fetchDashboard = async () => {
    const params = {
      type: 'book',
    }
    await Dashboard.getAllDashboardCount('bookDashboard', params)
  }

  useEffect(() => {
    fetchDashboard()
  }, [])

  const items: IGridItem[] = [
    {
      title: 'Total Books Listed',
      value: dashboard?.totalBook || '0',
      Icon: 'book',
      IconBg: 'blueLight',
    },
    {
      title: 'Pending Book Request',
      value: dashboard?.totalPendingBook || '0',
      Icon: 'book',
      IconBg: 'redLight',
    },
    {
      title: 'Total Book Lend',
      value: dashboard?.totalLendBook || '0',
      Icon: 'book',
      IconBg: 'greenLight',
    },
    {
      title: 'Total Book Returned',
      value: dashboard?.totalReturnedBook || '0',
      Icon: 'book',
      IconBg: 'yellowLight',
    },
  ]

  const [searchParams, setSearchParams] = useState<any>({
    search: null,
  })

  const fetchCategories = async (params: any) => {
    await Category.getAll(params).catch(() => {})
  }

  useEffect(() => {
    if (!booksCategory?.length) {
      fetchCategories(searchParams)
    }
  }, [])

  const [initialValues, setInitialValues] = useState({
    bookName: '',
    arthur: '',
    category: '',
    desc: '',
    coverImg: '',
    images1: '',
    images2: '',
    images3: '',
    images4: '',
  })

  const submitHandler = async (values) => {
    setLoading(true)

    const payload: any = {
      name: values?.bookName,
      author: values?.arthur,
      description: values?.desc,
      category: values?.category,
    }

    if (values?.coverImg?.base64) {
      payload.coverImage = {
        extension: values?.coverImg?.extension,
        base64: values?.coverImg?.base64,
      }
    }

    const images = []
    const imageFields = ['images1', 'images2', 'images3', 'images4']
    imageFields.forEach((field, index) => {
      const image = values?.[field]

      if (image?.base64) {
        const imagePayload: any = {
          extension: image.extension,
          base64: image.base64,
        }

        if (book?.additionalImages?.[index]) {
          imagePayload.id = book?.additionalImages[index]?.id
        }
        images.push(imagePayload)
      }
    })

    if (images.length > 0) {
      payload.images = images
    }

    if (!!id) {
      await Book.updateBook(payload, Number(id))
        .then(() => {
          navigate('/admin/book-managements')
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    } else {
      await Book.createBook(payload)
        .then(() => {
          navigate('/admin/book-managements')
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }
  }

  useEffect(() => {
    if (!!id && book?.id) {
      setInitialValues({
        ...initialValues,
        bookName: book?.name,
        arthur: book?.author,
        category: book?.categoryData?.id,
        desc: book?.description,
        coverImg: null,
        images1: null,
        images2: null,
        images3: null,
        images4: null,
      })
    }
  }, [])

  return (
    <>
      <StyleColumn>
        <GridCard items={items} />
        <Card customStyle={cardStyle}>
          <StyleCardTitle>
            {!!id ? 'Update' : 'Add'} Book Details
          </StyleCardTitle>
          <Formik
            initialValues={initialValues}
            onSubmit={submitHandler}
            enableReinitialize
          >
            {({ handleSubmit }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <StyleColumn>
                    <StyleFormRowWrapper>
                      <Column styleColumn={style60Percent}>
                        <StyleFormRowWrapper>
                          <StyleFormFieldWrapper width={100}>
                            <Fields.Input
                              name="bookName"
                              label="Book Name"
                              placeholder="Enter Book Name"
                              styleWrapper="width:100%"
                            />
                          </StyleFormFieldWrapper>
                          <StyleFormFieldWrapper width={48}>
                            <Fields.Input
                              name="arthur"
                              label="Arthur"
                              placeholder="Enter Arthur Name"
                              styleWrapper="width:100%"
                            />
                          </StyleFormFieldWrapper>
                          <StyleFormFieldWrapper width={48}>
                            <Fields.DropDown
                              name="category"
                              options={booksCategory}
                              titleAsKey="name"
                              valueAsKey="id"
                              label="Book Category"
                              styleWrapper="width:100%"
                            />
                          </StyleFormFieldWrapper>
                          <StyleFormFieldWrapper width={100}>
                            <Fields.TextArea
                              name="desc"
                              label="Description"
                              styleWrapper="width:100%"
                            />
                          </StyleFormFieldWrapper>
                        </StyleFormRowWrapper>
                      </Column>

                      <Column styleColumn={style38Percent}>
                        <StyleFormRowWrapper customStyle={{ rowGap: 30 }}>
                          <StyleFormFieldWrapper width={100}>
                            <Fields.Upload
                              name="coverImg"
                              size={350}
                              varient="dashed"
                              label="Book Name"
                              value={
                                !!id && !!book?.coverImage
                                  ? `${process.env.BUCKET_URL}/${book?.coverImage}`
                                  : null
                              }
                              placeholder="Upload Book Cover"
                            />
                          </StyleFormFieldWrapper>

                          <StyleFormFieldWrapper width={23}>
                            <Fields.Upload
                              name="images1"
                              size={100}
                              placeholder="Upload"
                              value={
                                !!id && !!book?.additionalImages?.length
                                  ? `${process.env.BUCKET_URL}/${book?.additionalImages?.[0]?.images}`
                                  : null
                              }
                              varient="galleryCard"
                            />
                          </StyleFormFieldWrapper>

                          <StyleFormFieldWrapper width={23}>
                            <Fields.Upload
                              name="images2"
                              size={100}
                              varient="galleryCard"
                              placeholder="Upload"
                              value={
                                !!id &&
                                !!book?.additionalImages?.length &&
                                book?.additionalImages?.[1]?.images
                                  ? `${process.env.BUCKET_URL}/${book?.additionalImages?.[1]?.images}`
                                  : null
                              }
                            />
                          </StyleFormFieldWrapper>

                          <StyleFormFieldWrapper width={23}>
                            <Fields.Upload
                              name="images3"
                              size={100}
                              varient="galleryCard"
                              placeholder="Upload"
                              value={
                                !!id &&
                                !!book?.additionalImages?.length &&
                                book?.additionalImages?.[2]?.images
                                  ? `${process.env.BUCKET_URL}/${book?.additionalImages?.[2]?.images}`
                                  : null
                              }
                            />
                          </StyleFormFieldWrapper>

                          <StyleFormFieldWrapper width={23}>
                            <Fields.Upload
                              name="images4"
                              size={100}
                              varient="galleryCard"
                              placeholder="Upload"
                              value={
                                !!id &&
                                !!book?.additionalImages?.length &&
                                book?.additionalImages?.[3]?.images
                                  ? `${process.env.BUCKET_URL}/${book?.additionalImages?.[3]?.images}`
                                  : null
                              }
                            />
                          </StyleFormFieldWrapper>
                        </StyleFormRowWrapper>
                      </Column>
                    </StyleFormRowWrapper>

                    <Row>
                      <Row styleRow={{ marginLeft: 'auto' }}>
                        <Button
                          title="Save"
                          onClick={handleSubmit}
                          loading={loading}
                        />
                      </Row>
                    </Row>
                  </StyleColumn>
                </Form>
              )
            }}
          </Formik>
        </Card>
      </StyleColumn>
    </>
  )
}

export default AdminBookManagementForm
